import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  INWARD_BUSINESS_TZ,
  normalizeYmdIST,
  todayYmdIST,
} from "../../utils/inwardBackdatePermission";

dayjs.extend(utc);
dayjs.extend(timezone);

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function ymdFromCalendarParts(year, monthIndex, day) {
  return dayjs
    .tz(
      `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
      INWARD_BUSINESS_TZ,
    )
    .format("YYYY-MM-DD");
}

function formatDisplayYmd(ymd) {
  if (!ymd) return "";
  const d = dayjs.tz(ymd, INWARD_BUSINESS_TZ);
  return d.isValid() ? d.format("DD-MM-YYYY") : ymd;
}

export default function RestrictedPostingDateInput({
  field,
  value,
  onChange,
  showError,
  fieldError,
  gridClass,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const valueYmd = normalizeYmdIST(value);
  const todayYmd = todayYmdIST();

  const [viewMonth, setViewMonth] = useState(() => {
    if (valueYmd) return dayjs.tz(valueYmd, INWARD_BUSINESS_TZ).startOf("month");
    return dayjs().tz(INWARD_BUSINESS_TZ).startOf("month");
  });

  const navigationMin = field.navigationMin || "";
  const navigationMax = field.navigationMax || todayYmd;
  const isDateSelectable = field.isDateSelectable;

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  useEffect(() => {
    if (valueYmd) {
      setViewMonth(dayjs.tz(valueYmd, INWARD_BUSINESS_TZ).startOf("month"));
    }
  }, [valueYmd]);

  const year = viewMonth.year();
  const monthIndex = viewMonth.month();
  const daysInMonth = viewMonth.daysInMonth();
  const firstWeekday = dayjs.tz(`${year}-${String(monthIndex + 1).padStart(2, "0")}-01`, INWARD_BUSINESS_TZ).day();
  const emptyDays = Array.from({ length: firstWeekday }, (_, i) => i);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const viewMonthYmd = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
  const canGoPrev =
    !navigationMin ||
    viewMonthYmd > navigationMin.slice(0, 7);
  const canGoNext =
    !navigationMax ||
    viewMonthYmd < navigationMax.slice(0, 7);

  const dayStates = useMemo(() => {
    return days.map((day) => {
      const ymd = ymdFromCalendarParts(year, monthIndex, day);
      const selectable =
        typeof isDateSelectable === "function" ? isDateSelectable(ymd) : true;
      const isSelected = valueYmd === ymd;
      const isToday = todayYmd === ymd;
      return { day, ymd, selectable, isSelected, isToday };
    });
  }, [days, year, monthIndex, isDateSelectable, valueYmd, todayYmd]);

  const handleSelect = (ymd, selectable) => {
    if (!selectable || field.disabled) return;
    onChange({ target: { name: field.name, value: ymd } });
    setOpen(false);
  };

  return (
    <div key={field.name} className={gridClass} ref={containerRef}>
      <label className="form-popup-label mb-1.5 block">
        {field.label}
        {field.required && <span className="ml-1 text-red-500">*</span>}
      </label>

      <div className="relative">
        <input
          type="text"
          name={field.name}
          value={formatDisplayYmd(valueYmd)}
          readOnly
          disabled={field.disabled}
          onClick={() => !field.disabled && setOpen((prev) => !prev)}
          placeholder={field.placeholder || "Select posting date"}
          required={field.required}
          className={`w-full cursor-pointer ${field.disabled ? "cursor-not-allowed opacity-50" : ""} ${
            showError ? "!border-red-500 focus:!ring-red-200" : ""
          }`}
        />

        {open && !field.disabled ? (
          <div className="absolute z-[200] mt-1 w-full min-w-[280px] rounded-xl border border-gray-200 bg-white p-3 shadow-lg">
            <div className="mb-3 flex items-center justify-between">
              <button
                type="button"
                disabled={!canGoPrev}
                onClick={() => setViewMonth((m) => m.subtract(1, "month"))}
                className="rounded p-1 text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Previous month"
              >
                ←
              </button>
              <span className="text-sm font-semibold text-gray-800">
                {MONTH_NAMES[monthIndex]} {year}
              </span>
              <button
                type="button"
                disabled={!canGoNext}
                onClick={() => setViewMonth((m) => m.add(1, "month"))}
                className="rounded p-1 text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Next month"
              >
                →
              </button>
            </div>

            <div className="mb-1 grid grid-cols-7 gap-1">
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
                <div key={d} className="py-1 text-center text-[10px] font-semibold text-gray-500">
                  {d}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {emptyDays.map((i) => (
                <div key={`empty-${i}`} />
              ))}
              {dayStates.map(({ day, ymd, selectable, isSelected, isToday }) => (
                <button
                  key={day}
                  type="button"
                  disabled={!selectable}
                  onClick={() => handleSelect(ymd, selectable)}
                  className={`rounded py-1.5 text-center text-xs font-medium transition-colors ${
                    !selectable
                      ? "cursor-not-allowed text-gray-300"
                      : isSelected
                      ? "bg-emerald-600 text-white"
                      : isToday
                      ? "border border-emerald-400 text-emerald-800 hover:bg-emerald-50"
                      : "text-gray-800 hover:bg-gray-100"
                  }`}
                >
                  {day}
                </button>
              ))}
            </div>

            <button
              type="button"
              disabled={!isDateSelectable?.(todayYmd)}
              onClick={() => handleSelect(todayYmd, isDateSelectable?.(todayYmd))}
              className="mt-3 w-full rounded-lg border border-emerald-600 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-300"
            >
              Today
            </button>
          </div>
        ) : null}
      </div>

      {showError && <p className="mt-2 text-sm text-red-500">{fieldError}</p>}
    </div>
  );
}
